import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { samSort } from 'src/app/helper/sortingHelper';
import { AgentListFilter } from 'src/app/samwebDescription/samweb/agentListFilterType';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { SamwinUserAssistanceState } from 'src/app/store/samwin/samwin-user-assistance-status/samwin-user-assistance-status.state';
import { SessionState } from 'src/app/store/session/session.state';
import { AssistanceState } from 'src/generated/signalR/assistance-state';
import { StatusType } from 'src/generated/signalR/status-type';
import { UserAssistanceStateRealTimeData } from 'src/generated/signalR/user-assistance-state-real-time-data';
import { AgentListEntryRemoteDto } from '../../../../../../../generated/signalR/agent-list-entry-remote-dto';
import { SamwinAgentListState } from '../../../../../samwin/samwin-agent-list/samwin-agent-list.state';
import { AgentListSettingState } from '../../../brick-settings/specific-brick-settings/agent-list-setting/agent-list-setting.state';
import { FilterItemsCleared } from '../../brick-filter-state/grid-filter.actions';
import { GridFilterState, IFilterSettings } from '../../brick-filter-state/grid-filter.state';
import { GridSortState } from '../../brick-sort-state/grid-sort.state';
import { AgentListAllFilterCleared, AgentListBrickSearchValueChanged, AgentListEntrySelected, AgentListSelectNextEntry } from './agent-list-behaviour.actions';

export interface AgentListBehaviourStateModel {
   [brickInstanceId: string]: AgentListBehaviourInstance;
}

export interface AgentListBehaviourInstance {
   searchValue: string;
   selectedId: number;
}

@State<AgentListBehaviourStateModel>({
   name: 'agentListBehaviour',
   defaults: {},
})
@Injectable()
export class AgentListBehaviourState {
   constructor(private store: Store) {}

   private static getSpecificBehaviourStateOrDefaultInternal(state: AgentListBehaviourStateModel, brickInstanceId: string): AgentListBehaviourInstance {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return {
            searchValue: null,
            selectedId: null,
         };
      }
   }

   @Selector([GridFilterState.getBrickFilterConfiguration])
   public static getAgentListSelectedFilter(filterConfigFn) {
      // TODO: Adjust filter to new group
      const result: (brickInstanceId: string) => AgentListFilter[] = (brickInstanceId: string) => {
         const filterConfig: IFilterSettings = filterConfigFn(brickInstanceId, 'default', 'agentStatusFilter');
         return _.map(filterConfig.selectedValues, (x) => AgentListFilter.valueByFilterId(Number(x)));
      };
      return result;
   }

   @Selector([AgentListBehaviourState])
   public static getSearchValue(state: AgentListBehaviourStateModel) {
      return (brickInstanceId: string) => {
         return AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId)?.searchValue;
      };
   }

   @Selector([SamwinAgentListState.getAgentList, AgentListSettingState.getIsShowOwnAgentEnabled, SessionState.getAuthenticatedUserId])
   public static getAgentListFilteredIsOwnAgentEnabled(
      agentList: AgentListEntryRemoteDto[],
      isShowOwnAgentEnabledFn: (brickId: string) => boolean,
      userId: number
   ) {
      return (brickInstanceId: string) => {
         const isShowOwnAgentEnabled = isShowOwnAgentEnabledFn(brickInstanceId);
         return agentList.filter((entry) => isShowOwnAgentEnabled || entry.userId !== userId);
      };
   }

   @Selector([AgentListBehaviourState.getAgentListFilteredIsOwnAgentEnabled, AgentListSettingState.getGroupIdsForAgentList])
   public static getAgentListFilteredByGroupIds(
      filteredAgentList: (brickInstanceId: string) => AgentListEntryRemoteDto[],
      getGroupIdsForAgentList: (brickId: string) => number[]
   ) {
      return (brickInstanceId: string) => {
         const agentList = filteredAgentList(brickInstanceId);
         const groupIdsForAgentList = getGroupIdsForAgentList(brickInstanceId);
         return agentList.filter((entry) => _.isEmpty(groupIdsForAgentList) || _.some(entry.memberOfGroups, (x) => _.includes(groupIdsForAgentList, x)));
      };
   }

   @Selector([AgentListBehaviourState.getAgentListFilteredByGroupIds, AgentListBehaviourState.getSearchValue, AgentListBehaviourState.getFilterAgent])
   public static getAgentListFilteredBySearchResult(
      filteredAgentList: (brickInstanceId: string) => AgentListEntryRemoteDto[],
      getSearchValue: (brickInstanceId: string) => string,
      getFilterAgent: (agent: AgentListEntryRemoteDto, searchValue: string) => boolean
   ) {
      return (brickInstanceId: string) => {
         const agentList = filteredAgentList(brickInstanceId);
         const searchValue = getSearchValue(brickInstanceId);
         return agentList.filter((entry) => _.isEmpty(searchValue) || getFilterAgent(entry, searchValue));
      };
   }

   @Selector([
      AgentListBehaviourState.getAgentListFilteredBySearchResult,
      AgentListBehaviourState.getAgentListSelectedFilter,
      SamwinUserAssistanceState.getAssistanceStateByUserIdFn,
      AgentListBehaviourState.getFilterStatus,
      SessionState.getAuthenticatedUserId,
   ])
   public static getAgentListFilteredByFilter(
      filteredAgentList: (brickInstanceId: string) => AgentListEntryRemoteDto[],
      getAgentListSelectedFilter: (brickInstanceId: string) => AgentListFilter[],
      getAssistanceStateByUserId: (userId: number) => UserAssistanceStateRealTimeData,
      getFilterStatus: (
         agent: AgentListEntryRemoteDto,
         selectedAgentFilters: AgentListFilter[],
         userAssistanceStatus: UserAssistanceStateRealTimeData,
         ownAgent: AgentListEntryRemoteDto
      ) => boolean,
      userId: number
   ) {
      return (brickInstanceId: string) => {
         const agentList = filteredAgentList(brickInstanceId);
         const selectedUserStatus = getAgentListSelectedFilter(brickInstanceId);
         const ownAgent: AgentListEntryRemoteDto = agentList.find((agent) => agent.userId === userId);
         return agentList.filter((entry) => getFilterStatus(entry, selectedUserStatus, getAssistanceStateByUserId(entry.userId), ownAgent));
      };
   }

   @Selector([GridSortState.getBrickSortConfiguration])
   public static getSortedAgentList(customSorting: (brickInstanceId: string, gridName: string) => SortDescriptor) {
      return (brickInstanceId: string, agentList: AgentListEntryRemoteDto[]) => {
         const sortDescription = customSorting(brickInstanceId, 'default');
         const result = _(agentList).value();
         if (!_.isNil(sortDescription)) {
            const sortedResult = samSort(result, sortDescription.field, sortDescription.dir);
            if (!_.isEmpty(sortedResult)) {
               return _(sortedResult).value();
            }
         }
         return result;
      };
   }

   @Selector([AgentListBehaviourState.getAgentListFilteredByFilter, AgentListBehaviourState.getSortedAgentList])
   public static getFilteredAndSortedAgentList(
      filteredAgentList: (brickInstanceId: string) => AgentListEntryRemoteDto[],
      getSortedAgentList: (brickInstanceId: string, agentList: AgentListEntryRemoteDto[]) => AgentListEntryRemoteDto[]
   ) {
      return (brickInstanceId: string) => {
         const agentList = filteredAgentList(brickInstanceId);
         return getSortedAgentList(brickInstanceId, agentList);
      };
   }

   @Selector()
   private static getFilterAgent() {
      return (agent: AgentListEntryRemoteDto, searchValue: string) => {
         return _.split(searchValue.toLowerCase(), ' ').every(
            (searchPart) =>
               _.includes(agent.displayName.toLowerCase(), searchPart) ||
               _.includes(agent.directoryNumber.toLowerCase(), searchPart) ||
               _.includes(agent.email.toLowerCase(), searchPart) ||
               _.includes(agent.firstName.toLowerCase(), searchPart) ||
               _.includes(agent.lastName.toLowerCase(), searchPart) ||
               _.includes(agent.userName.toLowerCase(), searchPart)
         );
      };
   }

   @Selector()
   private static getFilterStatus() {
      return (
         agent: AgentListEntryRemoteDto,
         selectedAgentFilters: AgentListFilter[],
         userAssistanceStatus: UserAssistanceStateRealTimeData,
         ownAgent: AgentListEntryRemoteDto
      ) => {
         if (_.isEmpty(selectedAgentFilters)) {
            return true;
         }

         const statusFilterGroups = _.groupBy(selectedAgentFilters, (f) => f.group);
         return _.every(statusFilterGroups, (filters) => {
            if (_.isEmpty(filters)) {
               return true;
            }

            return _.some(filters, (filter) => {
               switch (filter) {
                  case AgentListFilter.GREEN:
                     return agent.userStatus.type === StatusType.LOGGED_ON_READY;
                  case AgentListFilter.YELLOW:
                     return agent.userStatus.type === StatusType.LOGGED_ON_NOT_READY && agent.userStatus.id !== UserStatusEnum.BUSY.value;
                  case AgentListFilter.RED:
                     return agent.userStatus.id === UserStatusEnum.BUSY.value;
                  case AgentListFilter.GRAY:
                     return agent.userStatus.type === StatusType.LOGGED_OFF && agent.userStatus.id !== UserStatusEnum.OFFLINE.value;
                  case AgentListFilter.OFFLINE:
                     return agent.userStatus.id === UserStatusEnum.OFFLINE.value;
                  case AgentListFilter.ASSISTANCE_ACTIVE:
                     return !_.isNil(userAssistanceStatus) && userAssistanceStatus.state !== AssistanceState.None;
                  case AgentListFilter.ACTIVE_CALL:
                     return LineStateEnum.fromEnumValue(agent.callState).isCallInProgress;
                  case AgentListFilter.ONLY_MY_GROUPS:
                     return ownAgent.memberOfGroups.filter((group) => agent.memberOfGroups.includes(group)).length > 0;
                  default:
                     return false;
               }
            });
         });
      };
   }

   @Selector([AgentListBehaviourState.getSelectedEntry])
   public static isEntrySelected(getSelectedEntry: (brickInstanceId: string) => number) {
      return (brickInstanceId: string) => {
         return !_.isNil(getSelectedEntry(brickInstanceId));
      };
   }

   @Selector([AgentListBehaviourState])
   public static getSelectedId(state: AgentListBehaviourStateModel) {
      return (brickInstanceId: string) => {
         const agentBrickInstance = AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
         return agentBrickInstance.selectedId;
      };
   }

   @Selector([AgentListBehaviourState.getSelectedId, AgentListBehaviourState.getFilteredAndSortedAgentList])
   public static getSelectedIndex(
      getSelectedID: (brickInstanceId: string) => number,
      getAgentListFiltered: (brickInstanceId: string) => AgentListEntryRemoteDto[]
   ) {
      return (brickInstanceId: string) => {
         return _(getAgentListFiltered(brickInstanceId)).findIndex((value) => value.userId === getSelectedID(brickInstanceId));
      };
   }

   @Selector([AgentListBehaviourState.getSelectedId, AgentListBehaviourState.getFilteredAndSortedAgentList])
   public static getSelectedEntry(
      getSelectedId: (brickInstanceId: string) => number,
      getAgentListFiltered: (brickInstanceId: string) => AgentListEntryRemoteDto[]
   ) {
      return (brickInstanceId: string) => {
         return _(getAgentListFiltered(brickInstanceId)).find((agent) => agent.userId === getSelectedId(brickInstanceId));
      };
   }

   @Selector([AgentListBehaviourState.getSelectedEntry])
   public static getSelectedAgentId(getSelectedEntry: (brickInstanceId: string) => AgentListEntryRemoteDto) {
      return (brickInstanceId: string) => {
         return getSelectedEntry(brickInstanceId)?.userId;
      };
   }

   @Selector([AgentListBehaviourState.getSelectedEntry])
   public static getSelectedAgentDn(getSelectedEntry: (brickInstanceId: string) => AgentListEntryRemoteDto) {
      return (brickInstanceId: string) => {
         return getSelectedEntry(brickInstanceId)?.directoryNumber;
      };
   }

   @Selector([AgentListBehaviourState])
   public static getAgentListSearchValue(state: AgentListBehaviourStateModel) {
      return (brickInstanceId: string) => {
         const agentBrickInstance = AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
         return agentBrickInstance.searchValue;
      };
   }

   @Action(AgentListBrickSearchValueChanged)
   @ImmutableContext()
   public agentListSearchValueChanged(ctx: StateContext<AgentListBehaviourStateModel>, action: AgentListBrickSearchValueChanged) {
      const agentBrickInstance = _.cloneDeep(AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      if (agentBrickInstance.searchValue === action.newSearchValue) {
         return;
      }
      ctx.setState((state: AgentListBehaviourStateModel) => {
         agentBrickInstance.searchValue = action.newSearchValue;
         state[action.brickInstanceId] = agentBrickInstance;
         return state;
      });
   }
   @Action(AgentListEntrySelected)
   @ImmutableContext()
   public agentListEntrySelected(ctx: StateContext<AgentListBehaviourStateModel>, action: AgentListEntrySelected) {
      const agentBrickInstance = _.cloneDeep(AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      if (agentBrickInstance.selectedId === action.userId) {
         return;
      }
      ctx.setState((state: AgentListBehaviourStateModel) => {
         agentBrickInstance.selectedId = action.userId;
         state[action.brickInstanceId] = agentBrickInstance;
         return state;
      });
   }

   @Action(AgentListAllFilterCleared)
   @ImmutableContext()
   public agentListClearAllFilters(ctx: StateContext<AgentListBehaviourStateModel>, action: AgentListAllFilterCleared) {
      this.store.dispatch(new AgentListBrickSearchValueChanged(action.brickInstanceId, null));
      return this.store.dispatch(new FilterItemsCleared(action.brickInstanceId, 'default'));
   }

   @Action(AgentListSelectNextEntry)
   @ImmutableContext()
   public agentListSelectNextEntry(ctx: StateContext<AgentListBehaviourStateModel>, action: AgentListSelectNextEntry) {
      const currentValues = this.store.selectSnapshot(AgentListBehaviourState.getFilteredAndSortedAgentList)(action.brickInstanceId);
      const agentState = AgentListBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId);
      let currentIndex = _(currentValues).findIndex((value) => value.userId === agentState.selectedId);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      const selectedAgent = currentValues[nextIndex]?.userId;
      if (_.isNil(selectedAgent)) {
         return;
      }
      return ctx.dispatch(new AgentListEntrySelected(action.brickInstanceId, selectedAgent));
   }
}
